import React, { useEffect, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import {
  Building2,
  Calendar,
  Eye,
  Mail,
  MapPin,
  Pencil,
  Phone,
  Power,
  Send,
  User,
  Users,
  KeyRound,
} from 'lucide-react';
import apiClient from '../../../stateManagers/apiSetup/axiosInterceptor';
import { GlobalButton } from '../../../components/GlobalComponents/GlobalButton';

// ── Design tokens (matches Add/Edit Staff / Leads / Members) ──────────────────
const C = {
  teal: '#115550',
  tealMid: '#16706a',
  tealLight: '#c6e3e0',
  lime: '#c8e94b',
  limeDim: 'rgba(200,233,75,0.18)',
  limeBorder: 'rgba(200,233,75,0.4)',
  white: '#ffffff',
  muted: '#9ca3af',
  sub: '#6b7280',
};
const G = {
  shellBg: '#f8f9f9',
  shellBorder: '#e8ebeb',
  line: '#e5e7eb',
  soft: '#eef0f0',
};
const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

// ── Types (matches GET /api/v1/staff/:id response) ────────────────────────────
export interface StaffDetail {
  id: string;
  email: string | null;
  name: string | null;
  gender: string | null;
  address: string | null;
  phone: string | null;
  role: string | null;
  status: number | null;
  agency_id: string | null;
  agency_name: string | null;
  created_by_id: string | null;
  created_by_email: string | null;
  created_at: string | null;
  lead_stats?: {
    total: number;
    new: number;
    contacted: number;
    not_interested: number;
    closed: number;
  };
}

interface ViewStaffModalProps {
  open: boolean;
  staffId: string | null;
  onClose: () => void;
  onEdit: (staff: StaffDetail) => void;
  onResetPassword: (staff: StaffDetail) => void;
  onChanged: () => void;
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtDate = (d: string | null) => {
  if (!d) return '-';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};
const AVCOLORS = ['#115550', '#16706a', '#1a8a82', '#2563eb', '#7c3aed', '#db2777', '#ea580c'];
const avc = (s: string | null) => {
  if (!s) return AVCOLORS[0];
  let h = 0;
  for (let i = 0; i < s.length; i++) h = s.charCodeAt(i) + ((h << 5) - h);
  return AVCOLORS[Math.abs(h) % AVCOLORS.length];
};
const displayName = (d: StaffDetail) => (d.name && d.name.trim()) || d.email?.split('@')[0] || '-';
const getInitials = (d: StaffDetail) => displayName(d).substring(0, 2).toUpperCase();

const iconBox = (size = 29.5): React.CSSProperties => ({
  width: size,
  height: size,
  borderRadius: '7.4px',
  flexShrink: 0,
  background: `linear-gradient(135deg, ${C.teal}, ${C.tealMid})`,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: C.lime,
});

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string }> = ({ icon, title }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '9.2px',
      marginBottom: '13px',
      paddingBottom: '11px',
      borderBottom: `1px solid ${G.soft}`,
    }}
  >
    <div style={iconBox()}>{icon}</div>
    <span
      style={{
        color: C.teal,
        fontWeight: 700,
        fontSize: '0.71rem',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
      }}
    >
      {title}
    </span>
  </div>
);

const InfoRow: React.FC<{ icon: React.ComponentType<{ size?: number; color?: string }>; label: string; value: React.ReactNode }> = ({ icon: Icon, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0', borderBottom: `1px solid ${G.soft}` }}>
    <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      <Icon size={12} color={C.teal} />
    </div>
    <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '82px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</span>
    <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value || '—'}</span>
  </div>
);

const STAT_TILES: { key: keyof NonNullable<StaffDetail['lead_stats']>; label: string }[] = [
  { key: 'total', label: 'Total' },
  { key: 'new', label: 'New' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'not_interested', label: 'Not Interested' },
  { key: 'closed', label: 'Closed' },
];

// ─────────────────────────────────────────────────────────────────────────────

const ViewStaffModal: React.FC<ViewStaffModalProps> = ({ open, staffId, onClose, onEdit, onResetPassword, onChanged }) => {
  const [detail, setDetail] = useState<StaffDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingStatus, setTogglingStatus] = useState(false);

  const fetchDetail = async (id: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/v1/staff/${id}`);
      if (res.data.success) setDetail(res.data.data);
      else setError('Failed to load member');
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to load member');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && staffId) {
      setDetail(null);
      fetchDetail(staffId);
    }
  }, [open, staffId]);

  const handleToggleStatus = async () => {
    if (!detail) return;
    const newStatus = detail.status === 0 ? 1 : 0;
    try {
      setTogglingStatus(true);
      setError(null);
      await apiClient.patch(`/v1/staff/${detail.id}/status`, { status: newStatus });
      setDetail({ ...detail, status: newStatus });
      onChanged();
    } catch (e: any) {
      setError(e.response?.data?.error || 'Failed to update status');
    } finally {
      setTogglingStatus(false);
    }
  };

  const headerTemplate = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: '13px' }}>
      <div
        style={{
          ...iconBox(44),
          borderRadius: '12px',
          boxShadow: '0 6px 18px rgba(17,85,80,0.25)',
        }}
      >
        <Eye size={20} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: '1.01rem', fontWeight: 700, color: '#111827', lineHeight: 1.2, marginBottom: '3px' }}>
          Member Details
        </div>
        <div style={{ fontSize: '0.71rem', color: C.sub, fontWeight: 500 }}>View profile &amp; activity</div>
      </div>
    </div>
  );

  const active = detail?.status !== 0;
  const initials = detail ? getInitials(detail) : '?';
  const avatarColor = detail ? avc(detail.name || detail.email) : AVCOLORS[0];

  return (
    <Dialog
      visible={open}
      onHide={onClose}
      header={headerTemplate}
      className="view-staff-dialog"
      maskClassName="view-staff-mask"
      style={{ width: '90vw', maxWidth: '560px' }}
      modal
      draggable={false}
      resizable={false}
      blockScroll
    >
      <style>{`
        .view-staff-mask { backdrop-filter: blur(3px); background-color: rgba(17,40,38,0.28) !important; }
        .view-staff-dialog.p-dialog { background: ${G.shellBg} !important; border: 1px solid ${G.shellBorder} !important; border-radius: 18px !important; padding: 5px !important; overflow: hidden; box-shadow: 0 24px 48px -12px rgba(17,85,80,0.25) !important; }
        .view-staff-dialog .p-dialog-header { background: white !important; border: 1px solid ${G.line}; border-bottom-color: ${G.soft}; border-radius: 13px 13px 0 0 !important; padding: 16px 18px !important; }
        .view-staff-dialog .p-dialog-header-icon { color: ${C.sub}; }
        .view-staff-dialog .p-dialog-content { background: white !important; border: 1px solid ${G.line}; border-top: none; border-radius: 0 0 13px 13px !important; padding: 12px !important; }
      `}</style>

      {loading && !detail ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '220px' }}>
          <ProgressSpinner style={{ width: '34px', height: '34px' }} strokeWidth="3" animationDuration=".8s" />
        </div>
      ) : error && !detail ? (
        <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />
      ) : detail ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
          {error && <Message severity="error" text={error} style={{ width: '100%', fontSize: '0.79rem' }} />}

          {/* ── Profile ──────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px', display: 'flex', alignItems: 'center', gap: '11px', flexWrap: 'wrap' }}>
              <div
                style={{
                  width: '46px', height: '46px', borderRadius: '50%', background: avatarColor,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff',
                  fontSize: '0.9rem', fontWeight: 700, flexShrink: 0, boxShadow: `0 3px 10px ${avatarColor}55`,
                }}
              >
                {initials}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '0.95rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {displayName(detail)}
                </div>
                <div style={{ fontSize: '0.72rem', color: C.sub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {detail.email || '—'}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '5.5px', flexWrap: 'wrap' }}>
                <span
                  style={{
                    padding: '2.8px 9.2px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700,
                    textTransform: 'capitalize', background: 'rgba(17,85,80,0.08)', color: C.teal,
                    border: '1px solid rgba(17,85,80,0.2)',
                  }}
                >
                  {detail.role || 'staff'}
                </span>
                <button
                  onClick={handleToggleStatus}
                  disabled={togglingStatus}
                  title={active ? 'Click to deactivate' : 'Click to activate'}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: '3.7px',
                    padding: '2.8px 9.2px', borderRadius: '20px', fontSize: '0.63rem', fontWeight: 700,
                    background: active ? 'rgba(22,163,74,0.1)' : 'rgba(156,163,175,0.15)',
                    color: active ? '#15803d' : C.sub,
                    border: `1px solid ${active ? 'rgba(22,163,74,0.25)' : G.line}`,
                    cursor: togglingStatus ? 'not-allowed' : 'pointer',
                    opacity: togglingStatus ? 0.6 : 1,
                  }}
                >
                  <Power size={10} />
                  {togglingStatus ? '...' : active ? 'Active' : 'Inactive'}
                </button>
              </div>
            </div>
          </div>

          {/* ── Details ──────────────────────────────────────────────── */}
          <div style={SHELL}>
            <div style={{ ...INNER, padding: '16px' }}>
              <SectionHeader icon={<User size={14} />} title="Details" />
              <InfoRow icon={Phone} label="Phone" value={detail.phone} />
              <InfoRow icon={Users} label="Gender" value={detail.gender} />
              <InfoRow icon={MapPin} label="Address" value={detail.address} />
              <InfoRow icon={Building2} label="Agency" value={detail.agency_name} />
              <InfoRow
                icon={Send}
                label="Added By"
                value={detail.created_by_email ? detail.created_by_email.split('@')[0] : null}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', padding: '8.3px 0' }}>
                <div style={{ width: '26px', height: '26px', background: C.limeDim, borderRadius: '6.5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Calendar size={12} color={C.teal} />
                </div>
                <span style={{ fontSize: '0.67rem', color: C.muted, fontWeight: 600, width: '82px', flexShrink: 0, textTransform: 'uppercase', letterSpacing: '0.4px' }}>Joined</span>
                <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#1f2937', flex: 1 }}>{fmtDate(detail.created_at)}</span>
              </div>
            </div>
          </div>

          {/* ── Lead Stats ───────────────────────────────────────────── */}
          {detail.lead_stats && (
            <div style={SHELL}>
              <div style={{ ...INNER, padding: '16px' }}>
                <SectionHeader icon={<Mail size={14} />} title="Assigned Leads" />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(96px,1fr))', gap: '6.5px' }}>
                  {STAT_TILES.map((t) => (
                    <div
                      key={t.key}
                      style={{
                        padding: '9.2px', background: G.shellBg, border: `1px solid ${G.line}`,
                        borderRadius: '8.3px', textAlign: 'center',
                      }}
                    >
                      <div style={{ fontSize: '1.05rem', fontWeight: 700, color: C.teal }}>{detail.lead_stats![t.key]}</div>
                      <div style={{ fontSize: '0.6rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{t.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* ── Actions ──────────────────────────────────────────────── */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '7.4px', flexWrap: 'wrap' }}>
            <GlobalButton
              label="Reset Password"
              variant="secondary"
              icon={KeyRound}
              onClick={() => onResetPassword(detail)}
            />
            <GlobalButton
              label="Edit"
              variant="primary"
              icon={Pencil}
              onClick={() => onEdit(detail)}
            />
          </div>
        </div>
      ) : null}
    </Dialog>
  );
};

export default ViewStaffModal;